import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Calculator, RefreshCw } from 'lucide-react';
import { fetchAttendanceDaysForPeriod } from '../lib/attendanceApi';
import type { DayRow } from '../lib/types';
import type { AdminEmployee } from '../types/models';
import {
  fetchEmployeeReportAssignments,
  formatAssignmentRatio,
  isAssignmentActiveInPeriod,
  REPORT_ROLE_LABELS,
  type EmployeeReportAssignment
} from './reportAssignments';
import {
  buildStaffingEntries,
  getAllocatedMinutes,
  getMonthlyMinutes,
  staffingTotalEquiv,
  summarizeStaffingEntries,
  type CareCategory,
  type StaffingMode
} from './staffingConversion';

const CATEGORY_LABELS: Record<CareCategory, string> = {
  care: '介護',
  nurse: '看護'
};

const MODE_LABELS: Record<StaffingMode, string> = {
  fulltime_dedicated: '常勤専従',
  fulltime_concurrent: '常勤兼務',
  parttime: '非常勤'
};

const getCurrentMonthString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
};

const getMonthEndDate = (month: string): string => {
  const [yStr, mStr] = month.split('-');
  const lastDay = new Date(Number(yStr), Number(mStr), 0).getDate();
  return `${yStr}-${mStr}-${String(lastDay).padStart(2, '0')}`;
};

const getMonthStartDate = (month: string): string => `${month}-01`;

const getDaysInMonth = (month: string): number => {
  const [yStr, mStr] = month.split('-');
  return new Date(Number(yStr), Number(mStr), 0).getDate();
};

const defaultBaseHours = (days: number): number => {
  // 28・29日月=160時間、30・31日月=176時間
  return days <= 29 ? 160 : 176;
};

const isActiveInPeriod = (
  employee: AdminEmployee,
  periodStart: string,
  periodEnd: string
): boolean => {
  if (employee.hireDate && employee.hireDate > periodEnd) return false;
  if (employee.retirementDate && employee.retirementDate < periodStart) return false;
  if (employee.status === 'retired' && !employee.retirementDate) return false;
  return true;
};

const fmt2 = (value: number): string =>
  (Math.floor(value * 100) / 100).toFixed(2);

const StaffingConversionSection: React.FC<{ employees: AdminEmployee[] }> = ({ employees }) => {
  const [targetMonth, setTargetMonth] = useState<string>(() => getCurrentMonthString());
  const [baseHoursOverride, setBaseHoursOverride] = useState<number | null>(null);
  const [reportAssignments, setReportAssignments] = useState<EmployeeReportAssignment[]>([]);
  const [assignmentsPeriodKey, setAssignmentsPeriodKey] = useState<string>('');
  const [daysByEmployee, setDaysByEmployee] = useState<Record<string, DayRow[]>>({});
  const [assignmentLoading, setAssignmentLoading] = useState<boolean>(false);
  const [attendanceLoading, setAttendanceLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState<number>(0);

  const periodStart = useMemo(() => getMonthStartDate(targetMonth), [targetMonth]);
  const periodEnd = useMemo(() => getMonthEndDate(targetMonth), [targetMonth]);
  const daysInMonth = useMemo(() => getDaysInMonth(targetMonth), [targetMonth]);
  const baseHours = baseHoursOverride ?? defaultBaseHours(daysInMonth);

  const activeEmployees = useMemo(
    () => employees.filter(employee => isActiveInPeriod(employee, periodStart, periodEnd)),
    [employees, periodStart, periodEnd]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setAssignmentLoading(true);
      setError(null);
      setAssignmentsPeriodKey('');
      try {
        const assignments = await fetchEmployeeReportAssignments(periodStart, periodEnd);
        if (!cancelled) {
          setReportAssignments(assignments);
          setAssignmentsPeriodKey(`${periodStart}:${periodEnd}`);
        }
      } catch (err: any) {
        if (!cancelled) {
          setReportAssignments([]);
          setAssignmentsPeriodKey(`${periodStart}:${periodEnd}`);
          setError(err?.message ?? '兼務設定の取得に失敗しました。');
        }
      } finally {
        if (!cancelled) setAssignmentLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [periodStart, periodEnd, reloadToken]);

  const staffingEntries = useMemo(
    () => buildStaffingEntries(activeEmployees, reportAssignments, periodStart, periodEnd),
    [activeEmployees, reportAssignments, periodStart, periodEnd]
  );

  const trackedEmployees = useMemo(() => {
    const byId = new Map<string, AdminEmployee>();
    staffingEntries
      .filter(entry => entry.mode !== 'fulltime_dedicated')
      .forEach(entry => byId.set(entry.employee.id, entry.employee));
    return Array.from(byId.values());
  }, [staffingEntries]);

  useEffect(() => {
    let cancelled = false;
    const currentPeriodKey = `${periodStart}:${periodEnd}`;

    if (assignmentsPeriodKey !== currentPeriodKey) {
      setDaysByEmployee({});
      setAttendanceLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const run = async () => {
      setAttendanceLoading(true);
      const results: Record<string, DayRow[]> = {};
      let failedCount = 0;

      await Promise.all(
        trackedEmployees.map(async employee => {
          const code = employee.employeeCode?.trim();
          if (!code) {
            results[employee.id] = [];
            return;
          }
          try {
            results[employee.id] = await fetchAttendanceDaysForPeriod(
              code,
              periodStart,
              periodEnd
            );
          } catch {
            results[employee.id] = [];
            failedCount += 1;
          }
        })
      );

      if (!cancelled) {
        setDaysByEmployee(results);
        if (failedCount > 0) {
          setError(`${failedCount}名分の労働時間を取得できませんでした。`);
        }
        setAttendanceLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [trackedEmployees, periodStart, periodEnd, assignmentsPeriodKey]);

  const summary = useMemo(
    () => summarizeStaffingEntries(staffingEntries, daysByEmployee, baseHours),
    [staffingEntries, daysByEmployee, baseHours]
  );

  const assignmentsByEmployee = useMemo(() => {
    const result = new Map<string, EmployeeReportAssignment[]>();
    reportAssignments
      .filter(assignment => isAssignmentActiveInPeriod(assignment, periodStart, periodEnd))
      .forEach(assignment => {
        const current = result.get(assignment.employeeId) ?? [];
        current.push(assignment);
        result.set(assignment.employeeId, current);
      });
    return result;
  }, [reportAssignments, periodStart, periodEnd]);

  const loading = assignmentLoading || attendanceLoading;
  const sumCategories = (getter: (category: CareCategory) => number): number =>
    getter('care') + getter('nurse');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">常勤換算（介護・看護）</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">対象月</label>
            <input
              type="month"
              value={targetMonth}
              onChange={event => setTargetMonth(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">集計期間</label>
            <div className="px-3 py-2 text-sm text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
              {periodStart} 〜 {periodEnd}（{daysInMonth}日）
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">基準時間（時間/月）</label>
            <input
              type="number"
              value={baseHours}
              onChange={event => {
                const value = Number(event.target.value);
                setBaseHoursOverride(Number.isFinite(value) && value > 0 ? value : null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              min={1}
              step={1}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => setReloadToken(value => value + 1)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              再集計
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          既定値は28日・29日=160時間 / 30日・31日=176時間。常勤兼務は、月間実労働時間を登録済みの配分率で按分します。
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">常勤換算結果</h3>
          <p className="text-sm text-gray-500 mt-1">
            {periodStart} 〜 {periodEnd}｜基準時間 {baseHours}時間
          </p>
        </div>
        <div className="p-6 overflow-x-auto">
          <table className="w-full border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">介護</th>
                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">看護</th>
                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">合算</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-medium">常勤専従（実人数）</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {summary[category].fulltimeDedicatedCount}
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {sumCategories(category => summary[category].fulltimeDedicatedCount)}
                </td>
              </tr>
              <tr className="bg-purple-50">
                <td className="border border-gray-300 px-4 py-2 font-medium">常勤兼務 労働時間（合計）</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {fmt2(summary[category].concurrentHours)} h
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {fmt2(sumCategories(category => summary[category].concurrentHours))} h
                </td>
              </tr>
              <tr className="bg-purple-50">
                <td className="border border-gray-300 px-4 py-2 font-medium">常勤兼務 常勤換算</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {fmt2(summary[category].concurrentEquiv)}
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {fmt2(sumCategories(category => summary[category].concurrentEquiv))}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-medium">非常勤 労働時間（合計）</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {fmt2(summary[category].parttimeHours)} h
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {fmt2(sumCategories(category => summary[category].parttimeHours))} h
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-medium">非常勤 常勤換算</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {fmt2(summary[category].parttimeEquiv)}
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {fmt2(sumCategories(category => summary[category].parttimeEquiv))}
                </td>
              </tr>
              <tr className="bg-blue-50 font-bold">
                <td className="border border-gray-300 px-4 py-2">常勤換算 合計</td>
                {(['care', 'nurse'] as CareCategory[]).map(category => (
                  <td key={category} className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                    {fmt2(staffingTotalEquiv(summary[category]))}
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                  {fmt2(sumCategories(category => staffingTotalEquiv(summary[category])))}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500">
            ※ 常勤専従は1名=1、常勤兼務・非常勤は職種別計上時間÷基準時間で算出。小数点第2位まで表示（切り捨て）。
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">対象者内訳</h3>
          <p className="text-sm text-gray-500 mt-1">
            対象月に在籍している介護・看護の専従者・兼務者・非常勤職員
          </p>
        </div>
        <div className="max-h-[65vh] overflow-auto overscroll-contain p-6">
          <table className="w-full border-collapse min-w-[1050px]">
            <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">氏名</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">対象職種</th>
                <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">勤務形態</th>
                <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">兼務内容</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-medium text-gray-700">月間総労働時間</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-medium text-gray-700">職種別計上時間</th>
                <th className="border border-gray-300 px-4 py-2 text-right font-medium text-gray-700">常勤換算</th>
              </tr>
            </thead>
            <tbody>
              {staffingEntries.length === 0 && (
                <tr>
                  <td colSpan={7} className="border border-gray-300 px-4 py-6 text-center text-gray-500">
                    対象者がいません。
                  </td>
                </tr>
              )}
              {staffingEntries.map(entry => {
                const employeeDays = daysByEmployee[entry.employee.id] ?? [];
                const tracksHours = entry.mode !== 'fulltime_dedicated';
                const totalHours = getMonthlyMinutes(employeeDays) / 60;
                const allocatedHours = getAllocatedMinutes(entry, employeeDays) / 60;
                const equivalent = entry.mode === 'fulltime_dedicated'
                  ? 1
                  : baseHours > 0
                    ? allocatedHours / baseHours
                    : 0;
                const employeeAssignments = assignmentsByEmployee.get(entry.employee.id) ?? [];
                const assignmentSummary = employeeAssignments.length > 0
                  ? employeeAssignments.map(assignment => {
                      const ratio = formatAssignmentRatio(assignment);
                      return `${REPORT_ROLE_LABELS[assignment.reportRole]}${ratio ? ` ${ratio}` : ''}`;
                    }).join(' / ')
                  : entry.employee.jobType;

                return (
                  <tr key={entry.key}>
                    <td className="border border-gray-300 px-4 py-2">{entry.employee.name}</td>
                    <td className="border border-gray-300 px-4 py-2">{CATEGORY_LABELS[entry.category]}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                        entry.mode === 'fulltime_concurrent'
                          ? 'bg-purple-100 text-purple-700'
                          : entry.mode === 'fulltime_dedicated'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                      }`}>
                        {MODE_LABELS[entry.mode]}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-sm">{assignmentSummary}</td>
                    <td className="border border-gray-300 px-4 py-2 text-right tabular-nums">
                      {tracksHours ? `${fmt2(totalHours)} h` : '—'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right tabular-nums">
                      {tracksHours ? `${fmt2(allocatedHours)} h` : '—'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right tabular-nums">
                      {fmt2(equivalent)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StaffingConversionSection;
